import { useMemo, useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "firebaseDb";

import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useMaterialUIController } from "context";

import { useNavigate } from "react-router-dom";

function Orchards({ orchards, varieties }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const navigate = useNavigate();
  const [ownerUsers, setOwnerUsers] = useState([]);
  const filteredOrchards = useMemo(
    () =>
      orchards.filter((orchard) =>
        (orchard.siteCode + orchard.siteName).toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [orchards, searchQuery]
  );

  const pagedOrchards = filteredOrchards.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    const loadOwners = async () => {
      const usersRef = collection(db, "Users");
      const ownersSnapshot = await getDocs(query(usersRef, where("permissions", "==", "Owner")));
      setOwnerUsers(
        ownersSnapshot.docs.map((ownerDoc) => ({
          uid: ownerDoc.id,
          name: ownerDoc.data().name || ownerDoc.data().displayName || ownerDoc.id,
        }))
      );
    };

    loadOwners().catch((error) => {
      console.error("Error loading owner users:", error);
    });
  }, []);

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" fontWeight="medium">
          Orchard Information
        </MDTypography>
        <TextField
          id="search-orchards"
          label="Search Orchards"
          size="medium"
          sx={{
            minWidth: { xs: "100%", sm: 360 },
            "& .MuiInputBase-root": {
              backgroundColor: "rgba(76, 175, 80, 0.08)",
            },
            "& .MuiInputLabel-root": {
              fontSize: "1.2rem",
            },
          }}
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setPage(0);
          }}
        />
      </MDBox>
      <MDButton
        variant="contained"
        color="success"
        onClick={() => {
          navigate("/setup/addorchard/", {
            state: { varieties },
          });
        }}
      >
        <Icon>add</Icon>&nbsp;Add Orchard
      </MDButton>
      <MDBox mt={1}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead sx={{ display: "table-header-group", padding: 0, borderRadius: 0 }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Blocks</TableCell>
                <TableCell>Allowed Users</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedOrchards.map((orchard) => (
                <TableRow key={orchard.key} hover>
                  <TableCell>{orchard.siteName}</TableCell>
                  <TableCell>{orchard.siteCode}</TableCell>
                  <TableCell>{orchard.blocks?.length || 0}</TableCell>
                  <TableCell>
                    {orchard.ownerUIDs
                      ?.map((uid) => ownerUsers.find((user) => user.uid === uid)?.name || uid)
                      .join(", ") || 0}
                  </TableCell>
                  <TableCell align="right">
                    <MDButton
                      variant="text"
                      color={darkMode ? "white" : "dark"}
                      onClick={() => {
                        navigate(`/setup/orchard/${orchard.key}`, {
                          state: { site: orchard, varieties },
                        });
                      }}
                    >
                      <Icon>edit</Icon>&nbsp;edit
                    </MDButton>
                  </TableCell>
                </TableRow>
              ))}
              {pagedOrchards.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No orchards found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredOrchards.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </MDBox>
    </MDBox>
  );
}

Orchards.propTypes = {
  orchards: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  varieties: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Orchards;
